import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Building2,
  FactoryIcon,
  GraduationCap,
  FileStack,
  Wallet,
  MapPinned,
  Network,
  Layers,
  Search,
  Compass,
  Globe2,
  Settings2,
} from "lucide-react";
import { PageHero, SectionWrapper, SectionHeader } from "../components/Section";

const INSTITUTIONAL_FOCUS = [
  {
    icon: Building2,
    title: "Municipalities and public-service organizations",
  },
  {
    icon: FactoryIcon,
    title: "Organized industrial zones and utility-based environments",
  },
  {
    icon: GraduationCap,
    title:
      "Universities, hospitals, chambers, unions, and multi-department institutions",
  },
  {
    icon: FileStack,
    title: "Electronic document, archive, and records management environments",
  },
  {
    icon: Wallet,
    title: "Finance, revenue, procurement, and asset workflows",
  },
  {
    icon: MapPinned,
    title: "Field, infrastructure, laboratory, and spatial operations",
  },
  {
    icon: Network,
    title:
      "Institutional portals, mobile access, external integrations, and decision-support environments",
  },
  {
    icon: Layers,
    title:
      "Structured organizations with complex operating models that span departments, services, documents, and reporting",
  },
];

const STAGES = [
  {
    n: "01",
    icon: Search,
    title: "Understand the institution",
    body:
      "Allinstra begins by understanding the institution type, operating model, departments, workflows, documents, service needs, current systems, and priority challenges.",
  },
  {
    n: "02",
    icon: Compass,
    title: "Shape the scope",
    body:
      "Relevant modules, solution pathways, integration needs, user roles, and implementation priorities are evaluated according to the institution’s operational reality.",
  },
  {
    n: "03",
    icon: Globe2,
    title: "Adapt to the market",
    body:
      "Local legislation, language, public infrastructure, data access, digital trust services, and technical feasibility are reviewed before implementation assumptions are made.",
  },
  {
    n: "04",
    icon: Settings2,
    title: "Build for adoption",
    body:
      "The selected structure is configured, localized, piloted, and improved through phased implementation so the institution can adopt it with more confidence.",
  },
];

const About = () => (
  <main data-testid="page-about">
    <PageHero
      overline="About Allinstra"
      title="Institutional systems for organizations that need structure beyond standalone software."
      lead="Allinstra is built for municipalities, organized industrial zones, universities, hospitals, chambers, unions, public-service organizations, and structured institutions that need clearer operations, stronger document control, connected workflows, financial and administrative visibility, and decision-support capability."
    >
      <p className="max-w-3xl text-[15px] text-ink-600 leading-relaxed">
        Allinstra brings modular institutional system capabilities into a framework designed
        for complex organizations — where departments, documents, records, services, assets,
        field operations, integrations, and reporting need to move with more discipline and
        visibility.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/contact" data-testid="about-hero-contact" className="btn-primary">
          Contact Allinstra <ArrowUpRight size={16} />
        </Link>
        <Link
          to="/product-portfolio"
          data-testid="about-hero-portfolio"
          className="btn-secondary"
        >
          View Product Portfolio <ArrowRight size={14} />
        </Link>
      </div>
    </PageHero>

    {/* What Allinstra Is */}
    <SectionWrapper id="about-what">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeader
            overline="What Allinstra Is"
            title="A structured digital layer for institutional operations."
          />
        </div>
        <div className="lg:col-span-7 space-y-5 text-[16px] leading-relaxed text-ink-700">
          <p>
            Allinstra is a digital institutional systems brand focused on complex operational
            environments.
          </p>
          <p>
            It supports organizations that manage combinations of departments, documents,
            services, assets, subscribers, public-facing processes, finance workflows, field
            activity, portals, integrations, and management reporting.
          </p>
          <p className="text-ink-900">
            Its role is to help institutions move from fragmented process movement toward a
            more traceable, manageable, and implementation-aware digital structure.
          </p>
        </div>
      </div>
    </SectionWrapper>

    {/* Why Allinstra Exists */}
    <SectionWrapper id="about-why" alt>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeader
            overline="Why Allinstra Exists"
            title="Aligned institutions run better."
          />
        </div>
        <div className="lg:col-span-7 space-y-5 text-[16px] leading-relaxed text-ink-700">
          <p>
            Many institutions do not struggle because one single tool is missing. They struggle
            because operations, documents, approvals, services, field activity, finance
            processes, and reporting often sit in disconnected environments.
          </p>
          <p>
            Allinstra exists to bring those layers into a clearer institutional operating
            model.
          </p>
          <p className="text-ink-900">
            It focuses on operational clarity, document and records control, workflow
            continuity, institutional coordination, finance and procurement visibility, field
            and spatial process support, portals, external system readiness, reporting, and
            decision-support capability.
          </p>
        </div>
      </div>
    </SectionWrapper>

    {/* Institutional Systems Focus */}
    <SectionWrapper id="about-focus">
      <SectionHeader
        overline="Institutional Systems Focus"
        title="Built for institutions with complex operating structures."
      />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        {INSTITUTIONAL_FOCUS.map((f, i) => (
          <div
            key={i}
            data-testid={`about-institutional-focus-${i + 1}`}
            className="bg-white border border-ink-200 p-6 sm:p-7 flex items-start gap-4 card-hover"
          >
            <div className="w-10 h-10 border border-ink-200 inline-flex items-center justify-center text-cyan-600 shrink-0">
              <f.icon size={18} strokeWidth={1.5} />
            </div>
            <div>
              <span className="font-mono text-[10px] text-cyan-700 uppercase tracking-widest">
                {String(i + 1).padStart(2, "0")}
              </span>
              <p className="mt-1 text-[15px] text-ink-800 leading-relaxed">{f.title}</p>
            </div>
          </div>
        ))}
      </div>
    </SectionWrapper>

    {/* International Market Approach */}
    <SectionWrapper id="about-international" alt>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeader
            overline="International Market Approach"
            title="Adapt before deploy. Localize before scale."
          />
        </div>
        <div className="lg:col-span-7 space-y-5 text-[16px] leading-relaxed text-ink-700">
          <p>
            Allinstra is structured for selected markets and regional opportunities. The
            initial focus includes Kosovo, Türkiye, and the United Kingdom, while the wider
            expansion logic may cover selected opportunities across the European Union, the
            Balkans, Turkic States, and other international markets where institutional digital
            management systems are needed.
          </p>
          <p>
            Allinstra does not assume that every market has the same public infrastructure,
            e-government maturity, payment systems, electronic signature environment, identity
            and address services, land registry access, GIS capability, or third-party
            integration readiness.
          </p>
          <p className="text-ink-900">
            The Allinstra approach is based on evaluation, localization, implementation
            feasibility, and phased market adaptation.
          </p>
        </div>
      </div>
    </SectionWrapper>

    {/* How Allinstra Works */}
    <SectionWrapper id="about-how">
      <SectionHeader
        overline="How Allinstra Works"
        title="From institutional understanding to practical digital structure."
      />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {STAGES.map((s) => (
          <article
            key={s.n}
            data-testid={`about-stage-${s.n}`}
            className="bg-white border border-ink-200 p-7 sm:p-8 card-hover flex flex-col"
          >
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 border border-ink-200 inline-flex items-center justify-center text-cyan-600">
                <s.icon size={18} strokeWidth={1.5} />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-700">
                {s.n} / 04
              </div>
            </div>
            <h3 className="heading-display mt-5 text-lg sm:text-xl font-semibold leading-snug">
              {s.n.replace(/^0/, "")}. {s.title}
            </h3>
            <p className="mt-3 text-[14.5px] leading-relaxed text-ink-600">{s.body}</p>
          </article>
        ))}
      </div>
    </SectionWrapper>

    {/* Relationship with Erenown — placed after operational sections */}
    <SectionWrapper id="about-erenown" alt>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeader
            overline="Relationship with Erenown"
            title="Part of the Erenown brand structure."
          />
        </div>
        <div className="lg:col-span-7 space-y-5 text-[16px] leading-relaxed text-ink-700">
          <p>
            Allinstra is an institutional systems brand by Erenown.
          </p>
          <p>
            Within Erenown’s wider brand architecture, Allinstra focuses specifically on
            institutional systems, market adaptation, solution coordination, and long-term
            digital infrastructure for structured organizations.
          </p>
          <p className="text-ink-900">
            This relationship is also reflected in the website footer and legal structure.
          </p>
        </div>
      </div>
    </SectionWrapper>

    {/* Closing CTA */}
    <SectionWrapper id="about-closing-cta">
      <div className="border border-ink-200 p-8 sm:p-12 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="tag-overline">Closing</div>
            <h2 className="heading-display mt-3 text-2xl sm:text-3xl font-semibold">
              Bring order forward with Allinstra.
            </h2>
            <p className="mt-3 text-[15px] text-ink-600 leading-relaxed max-w-2xl">
              If your institution needs clearer operations, stronger document control,
              connected workflows, and better management visibility, Allinstra can help
              evaluate the right digital structure.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
            <Link to="/contact" data-testid="about-final-contact" className="btn-primary">
              Contact Allinstra <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/product-portfolio"
              data-testid="about-final-portfolio"
              className="btn-secondary"
            >
              View Product Portfolio
            </Link>
          </div>
        </div>
      </div>
    </SectionWrapper>
  </main>
);

export default About;
